import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import MockTestPage from './student/MockTestPage';

const C = { navy:'#0D1B2A', teal:'#00B4D8', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

export default function MockTestGuest() {
  const { token } = useParams();
  const storeKey = `mockguest_${token}`;
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem(storeKey) || '');
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);

  // Validate a stored session token; drop it if the session no longer exists.
  useEffect(() => {
    if (!sessionToken) return;
    api.get(`/api/guest/mock-tests/session/${sessionToken}/status`)
      .catch(() => { localStorage.removeItem(storeKey); setSessionToken(''); });
  }, [sessionToken, storeKey]);

  // Load link info for the landing page (only when not yet joined).
  useEffect(() => {
    if (sessionToken) return;
    api.get(`/api/guest/mock-tests/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setErr(e?.response?.data?.error || 'This mock test link is not available.'));
  }, [token, sessionToken]);

  const join = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    setJoining(true);
    try {
      const r = await api.post(`/api/guest/mock-tests/${token}/join`, { name: name.trim(), code: code.trim() });
      localStorage.setItem(storeKey, r.data.session_token);
      setSessionToken(r.data.session_token);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not join the test');
    } finally { setJoining(false); }
  };

  if (sessionToken) return <MockTestPage guestToken={sessionToken} />;

  const shell = (children) => (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:'Inter, system-ui, sans-serif' }}>
      <div style={{ width:'100%', maxWidth:440, background:'#fff', borderRadius:18, padding:'clamp(24px,5vw,40px)', boxShadow:'0 10px 40px rgba(0,0,0,0.08)' }}>{children}</div>
    </div>
  );

  if (err) return shell(<><h2 style={{margin:'0 0 8px',color:C.navy}}>Mock test unavailable</h2><p style={{color:C.grey,margin:0}}>{err}</p></>);
  if (!info) return shell(<p style={{color:C.grey,textAlign:'center',margin:0}}>Loading…</p>);
  if (info.full) return shell(<><h2 style={{margin:'0 0 8px',color:C.navy}}>This session is full</h2><p style={{color:C.grey,margin:0}}>All seats for this mock test have been taken. Please ask {info.school||'the school'} for another link.</p></>);

  const inp = { width:'100%', padding:'13px 14px', borderRadius:10, border:`1px solid ${C.border}`, fontSize:15, marginTop:6, boxSizing:'border-box' };
  return shell(
    <>
      <div style={{ fontSize:13, fontWeight:700, color:C.teal, letterSpacing:.5, textTransform:'uppercase' }}>{info.school || 'Mock Test'}</div>
      <h2 style={{ margin:'4px 0 4px', color:C.navy, fontSize:24 }}>{info.test_type || 'Mock Test'}{info.label ? ` — ${info.label}` : ''}</h2>
      <p style={{ color:C.grey, margin:'0 0 20px', fontSize:14 }}>
        Enter your name to join.{info.duration_minutes ? ` The test lasts about ${info.duration_minutes} minutes.` : ''} Your invigilator will start the test when everyone is ready.
      </p>
      <label style={{ fontSize:13, fontWeight:600, color:C.navy, display:'block' }}>Your name
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Aziz Karimov" style={inp} />
      </label>
      {info.requires_code && (
        <label style={{ fontSize:13, fontWeight:600, color:C.navy, display:'block', marginTop:14 }}>Access code
          <input value={code} onChange={e=>setCode(e.target.value)} placeholder="Code from your school" style={inp} />
        </label>
      )}
      <button onClick={join} disabled={joining} style={{ width:'100%', marginTop:22, padding:14, borderRadius:12, background:joining?C.grey:C.teal, color:'#fff', border:'none', fontWeight:700, fontSize:16, cursor:joining?'default':'pointer' }}>
        {joining ? 'Joining…' : 'Join Mock Test →'}
      </button>
      <p style={{ fontSize:11.5, color:'#94A3B8', marginTop:14, textAlign:'center' }}>No account needed. By joining you agree to take this test under exam conditions.</p>
    </>
  );
}
